import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, FileDown, Lock, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RINK_LABEL, RINK_BADGE } from "@/lib/rinks";

const PROTECTIVE = ["Helmet", "Pads", "Gloves", "None"];
const TREATMENTS = ["Ice Pack", "Bandage", "Splint", "CPR", "AED Used", "EMS Called", "Sent to Hospital", "Walked Off Rink"];

const EMPTY = {
  date: "", session_time: "", injured_name: "", age: "", gender: "", contact_number: "",
  parent_guardian: "", emergency_contact: "", description: "", witness_name: "", witness_phone: "",
  protective_equipment: [], protective_other: "", injury_location: [], treatment: [], treatment_other: "",
  medic_name: "", medic_role: "", medic_badge: "",
  camera_working: "", footage_viewed: "", footage_saved: "", cctv_notes: "",
  locked_by_admin: false,
  signature: "", report_date: "",
};

const CCTV_OPTS = ["Yes", "No", "N/A"];

const fmtDate = (d) => (d && d.includes("-") ? d.split("-").reverse().join("-") : d);
const todayIso = () => new Date().toISOString().slice(0, 10);

function IncidentForm({ rink, initial, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canWrite = user?.role === "admin" || user?.role === "medic";
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const readOnly = !canWrite || (!isAdmin && !!f.locked_by_admin);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) =>
    setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const save = async () => {
    for (const [k, label] of [["date", "Date"], ["session_time", "Session time"], ["injured_name", "Injured person's name"],
                              ["description", "Description"], ["medic_name", "Medic/First Responder name"],
                              ["medic_badge", "Medic badge number"]]) {
      if (!String(f[k] ?? "").trim()) { toast.error(`${label} is required`); return; }
    }
    if (!f.injury_location.length) { toast.error("Injury location is required — select at least one"); return; }
    setSaving(true);
    try {
      if (f.id) {
        const { id, rink: _r, created_by, created_at, updated_at, ...fields } = f;
        await api.patch(`/incidents/${id}`, fields);
        toast.success("Incident updated");
      } else {
        await api.post("/incidents", f, { params: { rink } });
        toast.success("Incident recorded");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const txt = (k, label, extra = {}) => (
    <label key={k} className="text-sm font-bold">{label}
      <input className="input-brutal mt-1 w-full" value={f[k]} onChange={(e) => set(k, e.target.value)}
             data-testid={`incident-${k.replace(/_/g, "-")}`} {...extra} />
    </label>
  );

  const checks = (k, options) => (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map((opt) => (
        <label key={opt} className={`badge-brutal cursor-pointer flex items-center gap-1.5 ${f[k].includes(opt) ? "bg-sky-300" : "bg-white"}`}
               data-testid={`incident-${k.replace(/_/g, "-")}-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
          <input type="checkbox" className="w-3.5 h-3.5 accent-slate-700" checked={f[k].includes(opt)} onChange={() => toggle(k, opt)} />
          {opt}
        </label>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" data-testid="incident-modal">
      <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a] p-6 w-full max-w-3xl bg-white my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl uppercase text-slate-900">{f.id ? "Edit" : "New"} Incident — {RINK_LABEL[rink]}</h2>
          <button onClick={onClose} className="hover:text-rose-600" data-testid="incident-cancel"><X className="w-5 h-5" /></button>
        </div>

        {isAdmin && (
          <label className="flex items-center gap-2 mb-4 font-bold text-sm cursor-pointer" data-testid="incident-lock-label">
            <input type="checkbox" className="w-4 h-4 accent-slate-700" checked={!!f.locked_by_admin}
                   onChange={(e) => set("locked_by_admin", e.target.checked)} data-testid="incident-locked-by-admin" />
            Locked By Admin
            <span className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">— medic can view but not edit</span>
          </label>
        )}
        {readOnly && (
          <div className="mb-4 badge-brutal bg-amber-300 text-slate-900 inline-flex items-center gap-1.5" data-testid="incident-locked-banner">
            <Lock className="w-3.5 h-3.5" /> Locked by admin — read only
          </div>
        )}

        <fieldset disabled={readOnly} className="space-y-5">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Session</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-bold">Date <span className="text-red-600">*</span>
                <input type="date" className="input-brutal mt-1 w-full" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="incident-date" />
              </label>
              <label className="text-sm font-bold">Session Time <span className="text-red-600">*</span>
                <input type="time" className="input-brutal mt-1 w-full" value={f.session_time} onChange={(e) => set("session_time", e.target.value)} data-testid="incident-session-time" />
              </label>
            </div>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Injured Person Information</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {txt("injured_name", "Full Name *")}
              {txt("age", "Age")}
              <label className="text-sm font-bold">Gender
                <select className="input-brutal mt-1 w-full" value={f.gender} onChange={(e) => set("gender", e.target.value)} data-testid="incident-gender">
                  <option value="">Select…</option>
                  {["Male", "Female", "Other"].map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </label>
              {txt("contact_number", "Contact Number")}
              {txt("parent_guardian", "Parent/Guardian (if under 18)")}
              {txt("emergency_contact", "Emergency Contact (Name & Phone)")}
            </div>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Injury Location *</div>
            {checks("injury_location", ["Head", "Neck", "Back", "Arm", "Wrist", "Leg", "Foot"])}
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Incident Details</div>
            <label className="text-sm font-bold">Describe what happened (include activity at the time) <span className="text-red-600">*</span>
              <textarea className="input-brutal mt-1 w-full" rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="incident-description" />
            </label>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Witnesses (if any)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {txt("witness_name", "Witness Name")}
              {txt("witness_phone", "Witness Phone")}
            </div>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Was protective equipment worn?</div>
            {checks("protective_equipment", PROTECTIVE)}
            {f.protective_equipment.includes("Other") === false && (
              <label className="text-sm font-bold mt-2 block">Other equipment
                <input className="input-brutal mt-1 w-full" value={f.protective_other} onChange={(e) => set("protective_other", e.target.value)} data-testid="incident-protective-other" />
              </label>
            )}
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Treatment provided</div>
            {checks("treatment", TREATMENTS)}
            <label className="text-sm font-bold mt-2 block">Other treatment
              <input className="input-brutal mt-1 w-full" value={f.treatment_other} onChange={(e) => set("treatment_other", e.target.value)} data-testid="incident-treatment-other" />
            </label>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">CCTV / Camera Footage</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[["camera_working", "Camera system working?"], ["footage_viewed", "Footage viewed?"], ["footage_saved", "Footage saved?"]].map(([k, label]) => (
                <label key={k} className="text-sm font-bold">{label}
                  <select className="input-brutal mt-1 w-full" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`incident-${k.replace(/_/g, "-")}`}>
                    <option value="">Select…</option>
                    {CCTV_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <label className="text-sm font-bold mt-2 block">CCTV notes (optional)
              <input className="input-brutal mt-1 w-full" value={f.cctv_notes} onChange={(e) => set("cctv_notes", e.target.value)} data-testid="incident-cctv-notes" />
            </label>
          </div>

          <div>
            <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500 mb-2">Medic/Staff Report</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {txt("medic_name", "Name of Medic/First Responder *")}
              {txt("medic_role", "Title/Role")}
              {txt("medic_badge", "Official Medic Badge Number *")}
              {txt("signature", "Signature (type name)")}
              <label className="text-sm font-bold">Report Date
                <input type="date" className="input-brutal mt-1 w-full" value={f.report_date} onChange={(e) => set("report_date", e.target.value)} data-testid="incident-report-date" />
              </label>
            </div>
          </div>
        </fieldset>

        <div className="flex gap-3 mt-6">
          {!readOnly && (
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="incident-save">
              {saving ? "Saving…" : f.id ? "Save changes" : "Save incident"}
            </button>
          )}
          <button onClick={onClose} className="btn-secondary" data-testid="incident-cancel-bottom">{readOnly ? "Close" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

export default function MedicalPage() {
  const { user } = useAuth();
  const isMedic = user?.role === "medic";
  const isAdmin = user?.role === "admin";
  const canWrite = isAdmin || isMedic;
  const [params] = useSearchParams();
  const rink = isMedic ? user.rink : (params.get("rink") || "sligo");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sumFrom, setSumFrom] = useState("");
  const [sumTo, setSumTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incidents", { params: { rink } });
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [rink]);

  useEffect(() => { load(); }, [load]);

  const count = (pred) => rows.filter(pred).length;
  const stats = [
    ["Total incidents", rows.length, "total"],
    ["EMS called", count((r) => (r.treatment || []).includes("EMS Called")), "ems"],
    ["Sent to hospital", count((r) => (r.treatment || []).includes("Sent to Hospital")), "hospital"],
    ["AED used", count((r) => (r.treatment || []).includes("AED Used")), "aed"],
    ["Helmet worn", count((r) => (r.protective_equipment || []).includes("Helmet")), "helmet"],
  ];

  const INJURY_LOCS = ["Head", "Neck", "Back", "Arm", "Wrist", "Leg", "Foot"];
  const locCounts = INJURY_LOCS.map((loc) => [loc, count((r) => (r.injury_location || []).includes(loc))]);

  const del = async (id) => {
    if (!window.confirm("Delete this incident record? This cannot be undone.")) return;
    try {
      await api.delete(`/incidents/${id}`);
      toast.success("Incident deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportPdf = async () => {
    try {
      const { data } = await api.get(`/incidents/${rink}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rink}-medical-incidents.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportSummaryPdf = async () => {
    try {
      const { data } = await api.get(`/incidents/${rink}/summary.pdf`, {
        params: { date_from: sumFrom || undefined, date_to: sumTo || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rink}-medical-summary.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6" data-testid="medical-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Medical</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900 flex items-center gap-3">
            {RINK_LABEL[rink]} <span className={`badge-brutal ${RINK_BADGE[rink]}`}>{rink}</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2">Medical incident reports. {isAdmin ? "Admin access only." : isMedic ? "Medic access — add and view incidents." : "View-only access."}</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          {isAdmin && (
            <>
              <div className="flex items-end gap-1.5 border-2 border-slate-900 bg-white px-2 py-1.5 shadow-[2px_2px_0px_0px_#0f172a]" data-testid="medical-summary-range">
                <label className="font-mono-tight uppercase text-[10px] tracking-widest text-slate-500">From
                  <input type="date" className="input-brutal mt-0.5 block" value={sumFrom} onChange={(e) => setSumFrom(e.target.value)} data-testid="medical-summary-from" />
                </label>
                <label className="font-mono-tight uppercase text-[10px] tracking-widest text-slate-500">To
                  <input type="date" className="input-brutal mt-0.5 block" value={sumTo} onChange={(e) => setSumTo(e.target.value)} data-testid="medical-summary-to" />
                </label>
                <button onClick={exportSummaryPdf} data-testid="medical-export-summary-pdf"
                        className="inline-flex items-center gap-1.5 bg-amber-400 text-slate-900 border-2 border-slate-900 font-bold uppercase text-xs px-3 py-2 tracking-wider hover:bg-amber-300 transition-colors">
                  <FileDown className="w-4 h-4" /> Summary PDF
                </button>
              </div>
              <button onClick={exportPdf} data-testid="medical-export-pdf"
                      className="inline-flex items-center gap-1.5 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a] hover:bg-slate-700 transition-colors">
                <FileDown className="w-4 h-4" /> Export PDF
              </button>
            </>
          )}
          {canWrite && (
            <button onClick={() => setEditing({ ...EMPTY, report_date: todayIso() })} data-testid="medical-add-btn"
                    className="inline-flex items-center gap-1.5 bg-sky-500 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a] hover:bg-sky-600 transition-colors">
              <Plus className="w-4 h-4" /> Add New Incident
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="medical-summary">
        {stats.map(([label, value, key]) => (
          <div key={key} className="card-brutal p-4 text-center shadow-[4px_4px_0px_0px_#0f172a]" data-testid={`medical-stat-${key}`}>
            <div className="font-mono-tight uppercase text-[10px] tracking-widest text-slate-500">{label}</div>
            <div className="font-display text-3xl mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="card-brutal p-3 mb-6 shadow-[4px_4px_0px_0px_#0f172a]" data-testid="medical-injury-locations">
        <div className="font-mono-tight uppercase text-[10px] tracking-widest text-slate-500 mb-2">Injury locations</div>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
          {locCounts.map(([loc, n]) => (
            <div key={loc} className="text-center border-2 border-slate-900 bg-sky-50 px-1 py-1.5" data-testid={`medical-loc-${loc.toLowerCase()}`}>
              <div className="font-mono-tight uppercase text-[10px] tracking-widest text-slate-600">{loc}</div>
              <div className="font-display text-xl">{n}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a]" data-testid="medical-table">
        <table className="w-full table-fixed border-collapse [&_th]:border-l-2 [&_th]:border-slate-900 [&_td]:border-l-2 [&_td]:border-slate-900 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0">
          <thead>
            <tr className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest">
              <th className="p-2.5 text-center font-bold w-[110px]">Date</th>
              <th className="p-2.5 text-center font-bold w-[80px]">Time</th>
              <th className="p-2.5 text-center font-bold">Injured Person</th>
              <th className="p-2.5 text-center font-bold w-[64px]">Age</th>
              <th className="p-2.5 text-center font-bold w-[80px]">Gender</th>
              <th className="p-2.5 text-center font-bold">Treatment</th>
              <th className="p-2.5 text-center font-bold w-[140px]">Medic</th>
              <th className="p-2.5 text-center font-bold w-[64px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="medical-loading">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="medical-empty">No incidents recorded</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t-2 border-slate-900 hover:bg-sky-50/60 transition-colors" data-testid={`medical-row-${r.id}`}>
                <td className="p-2.5 text-sm font-bold text-center whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="p-2.5 text-sm text-center">{r.session_time}</td>
                <td className="p-2.5 text-sm text-center font-bold">{r.injured_name}</td>
                <td className="p-2.5 text-sm text-center">{r.age}</td>
                <td className="p-2.5 text-sm text-center">{r.gender}</td>
                <td className="p-2.5 text-sm text-center">{[...(r.treatment || []), r.treatment_other].filter(Boolean).join(", ")}</td>
                <td className="p-2.5 text-sm text-center">{r.medic_name}{r.medic_badge ? ` (#${r.medic_badge})` : ""}</td>
                <td className="p-1.5 text-center">
                  {r.locked_by_admin && (
                    <span className="badge-brutal bg-amber-300 text-slate-900 mr-1.5 inline-flex items-center" title="Locked by admin" data-testid={`medical-locked-${r.id}`}>
                      <Lock className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <button onClick={() => setEditing({ ...EMPTY, ...r })} title={isAdmin || (isMedic && !r.locked_by_admin) ? "Edit incident" : "View incident"} data-testid={`medical-edit-${r.id}`}
                          className="bg-white border-2 border-slate-900 p-1.5 hover:bg-sky-100 shadow-[2px_2px_0px_0px_#0f172a] transition-colors inline-flex items-center justify-center">
                    {isAdmin || (isMedic && !r.locked_by_admin) ? <Pencil className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  {isAdmin && (
                    <button onClick={() => del(r.id)} title="Delete incident" data-testid={`medical-delete-${r.id}`}
                            className="bg-white border-2 border-slate-900 p-1.5 ml-1.5 hover:bg-rose-200 shadow-[2px_2px_0px_0px_#0f172a] transition-colors inline-flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <IncidentForm
          rink={rink}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
